export const generateSecret = (length: number) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmopqrstuvwxyz0123456789!()_=";
  let result = "";
  let counter = 0;
  while (counter < length) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
    counter++;
  }
  return result;
};

export const toIsoStringShort = (date: Date) => date.toLocaleDateString("en-CA");

export const formatDate = (value: string, locale?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return new Date(value).toLocaleDateString(locale, options);
};

export const formatTimeStamp = (value: string, locale?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  return new Date(value).toLocaleDateString(locale, options);
};

export const formatDateToMonthYear = (dateString: string, locale?: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);
};

export const getWeekNumber = (date: Date): number => {
  // Get the current year
  const year = date.getFullYear();

  // Get the first day of the year
  const firstDayOfYear = new Date(year, 0, 1);

  // Calculate the number of days between the given date and the first day of the year
  const daysBetween = Math.floor((date.getTime() - firstDayOfYear.getTime()) / (1000 * 3600 * 24));

  // Calculate the week number
  return Math.ceil((daysBetween + 1) / 7);
};

export const formatWeight = (weight: number): [string, string] => {
  const kg = Math.floor(weight);
  const rest = weight - kg;

  const kgString = kg.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const restString = rest
    .toLocaleString(undefined, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })
    .substring(1);

  return [kgString, restString];
};

export const downloadFileFromData = (data: string, fileName: string, contentType?: string) => {
  const toString = function (a: Object) {
    return String(a);
  };

  const myBlob = (window.Blob || toString).bind(window);

  const anchor = document.createElement("a");

  const d = window.atob(data);

  const tType = contentType || "application/octet-stream";

  if (/([\x80-\xff])/.test(d)) {
    const tempUiArr = new Uint8Array(d.length);
    const mx = tempUiArr.length;

    for (var i = 0; i < mx; i++) {
      tempUiArr[i] = d.charCodeAt(i);
    }

    const o = new myBlob([tempUiArr], { type: tType });
    anchor.href = window.URL.createObjectURL(o);
  } else {
    const o = new myBlob([d || ""], { type: tType });
    anchor.href = window.URL.createObjectURL(o);
  }

  anchor.setAttribute("download", fileName);
  anchor.className = "download-js-link";
  anchor.innerHTML = "downloading...";
  anchor.style.display = "none";
  document.body.appendChild(anchor);

  setTimeout(function () {
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(function () {
      window.URL.revokeObjectURL(anchor.href);
    }, 250);
  }, 66);

  return true;
};

export const getPreviousTrimesterDates = (): { firstDay: string; lastDay: string } => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-based: Jan = 0, Dec = 11
  const currentQuarter = Math.floor(currentMonth / 3); // 0 to 3
  const previousQuarter = (currentQuarter + 3) % 4;
  const yearOffset = currentQuarter === 0 ? -1 : 0;

  const year = now.getFullYear() + yearOffset;
  const firstMonth = previousQuarter * 3;

  const firstDay = new Date(year, firstMonth, 1);
  const lastDay = new Date(year, firstMonth + 3, 0); // day 0 of next quarter = last day of current quarter

  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  return {
    firstDay: formatDate(firstDay),
    lastDay: formatDate(lastDay),
  };
};

export const staticHolidays = [
  "01-01", // New Year's Day
  "01-06", // Epiphany
  "04-25", // Liberation Day
  "05-01", // Labour Day
  "06-02", // Republic Day
  "08-15", // Assumption of Mary
  "11-01", // All Saints' Day
  "12-08", // Immaculate Conception
  "12-25", // Christmas Day
  "12-26", // Saint Stephen's Day
];

// Calculates Easter Sunday for a given year
export function getEasterSunday(year: number): Date {
  const f = Math.floor;
  const G = year % 19;
  const C = f(year / 100);
  const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
  const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
  const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
  const L = I - J;
  const month = 3 + f((L + 40) / 44);
  const day = L + 28 - 31 * f(month / 4);
  return new Date(year, month - 1, day);
}

// Calculates movable holidays based on Easter
export function getMovableHolidays(year: number): string[] {
  const easterSunday = getEasterSunday(year);

  const easterMonday = new Date(easterSunday);
  easterMonday.setDate(easterSunday.getDate() + 1);

  const whitMonday = new Date(easterSunday);
  whitMonday.setDate(easterSunday.getDate() + 50);

  // Format dates as YYYY-MM-DD
  const format = (date: Date) => toIsoStringShort(date);

  return [
    format(easterSunday), // Easter Sunday
    format(easterMonday), // Easter Monday
    format(whitMonday), // Whit Monday
  ];
}

// Main function: checks if a date is a holiday
export function isHoliday(date: string): boolean {
  const [yearStr, monthStr, dayStr] = date.split("-");
  if (!yearStr || !monthStr || !dayStr) {
    return false;
  }
  const year = parseInt(yearStr, 10);
  const monthDay = `${monthStr}-${dayStr}`;

  // Check static holidays
  if (staticHolidays.includes(monthDay)) {
    return true;
  }

  // Check movable holidays
  const movable = getMovableHolidays(year);
  return movable.includes(date);
}
